import { matchesSearch } from "@car/api/search";
import {
  Command,
  CommandBareInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@car/ui/components/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@car/ui/components/dialog";
import { buttonVariants } from "@car/ui/components/button";
import { Image } from "@car/ui/components/image";
import { Kbd } from "@car/ui/components/kbd";
import { Skeleton } from "@car/ui/components/skeleton";
import { SEP } from "@car/ui/lib/utils";
import {
  ArrowLeft02Icon,
  Cancel01Icon,
  Message01Icon,
  Search01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { isConsultLink, openConsult } from "@/lib/consult";
import {
  bodyKeys,
  bodyLabels,
  fuelKeys,
  fuelLabels,
  headerMenu,
  platePriceWords,
  useSiteContent,
  type MenuLink,
  type SiteVehicle,
} from "@/lib/content";
import { orpc } from "@/utils/orpc";

// ---------------------------------------------------------------------------
// جستجوی سربرگ همان لحظه جستجو می‌کند: خودروها (نام، برند، تیپ، سوخت، بدنه)، دسته‌ها و صفحه‌ها
// و خدمات منو. با Ctrl+K یا «/» هم باز می‌شود. یکسان‌سازی متن فارسی همان matchesSearch پنل است.
// ---------------------------------------------------------------------------

const vehiclesQuery = orpc.site.vehicles.list.queryOptions({ input: {} });

/** یک مقصد جستجو: صفحه، دسته‌ی خودرو یا خدمت */
type Destination = {
  label: string;
  hint: string;
  href: string;
  external?: boolean;
  /** what people type for it besides its title («قسط» for the buying guide) */
  keywords?: readonly string[];
};

const shortcuts: Destination[] = [
  ...fuelKeys.map((key) => ({
    label: `خودروهای ${fuelLabels[key]}`,
    hint: "دسته",
    href: `/vehicles?fuel=${key}`,
  })),
  ...bodyKeys.map((key) => ({
    label: bodyLabels[key],
    hint: "نوع بدنه",
    href: `/vehicles?body=${key}`,
  })),
];

/** صفحه‌ها و خدمات منوی سربرگ (از مدیریت محتوا) به‌علاوه‌ی صفحه‌های ثابت؛ بی‌تکرار */
function useDestinations(): Destination[] {
  const content = useSiteContent();
  const menu = headerMenu(content.headerMenu);
  const fromMenu = [
    ...menu.groups.flatMap((group) => group.links.map((link) => ({ link, hint: group.title }))),
    ...menu.links.map((link) => ({ link, hint: "صفحه" })),
  ];
  const fixed: Destination[] = [
    { label: "همه خودروها", hint: "صفحه", href: "/vehicles" },
    {
      label: "رزرو تست‌درایو",
      hint: "از صفحه‌ی هر خودرو",
      href: "/vehicles",
      keywords: ["تست درایو", "test drive", "رانندگی با خودرو"],
    },
    { label: "مقایسه خودروها", hint: "صفحه", href: "/compare" },
    {
      label: "راهنمای خرید و اقساط",
      hint: "صفحه",
      href: "/guide",
      keywords: ["قسط", "وام", "پیش‌پرداخت", "تأمین مالی", "تسهیلات", "پلاک"],
    },
    {
      label: "تماس و نشانی شعب",
      hint: "صفحه",
      href: "/contact",
      keywords: ["آدرس", "تلفن", "شماره تماس", "ساعت کاری"],
    },
    {
      label: "لغو یا تغییر رزرو",
      hint: "تماس با شعبه",
      href: "/contact",
      keywords: ["کنسل", "لغو رزرو", "تغییر رزرو", "لغو تست درایو"],
    },
    {
      label: "حساب کاربری و پنل مشتری",
      hint: "صفحه",
      href: "/panel",
      keywords: ["ورود", "ثبت نام", "نوبت", "سرویس"],
    },
  ];
  const all = [
    ...fixed,
    ...fromMenu
      .filter(({ link }) => !isConsultLink(link) && !link.href.includes("#"))
      .map(({ link, hint }: { link: MenuLink; hint: string }) => ({
        label: link.label,
        hint,
        href: link.href,
        external: link.external,
      })),
  ];
  // دسته‌های سوخت و بدنه جدا می‌آیند؛ همان پیوند در منو تکرار نمی‌شود
  const taken = new Set(shortcuts.map((item) => item.href));
  return all.filter(
    (item, index) =>
      !taken.has(item.href) &&
      all.findIndex((other) => other.href === item.href && other.label === item.label) === index,
  );
}

const vehicleFields = (vehicle: SiteVehicle) => [
  vehicle.name,
  // «camry» هم باید همان خودرو را پیدا کند، نه فقط «کمری»
  vehicle.nameLatin,
  vehicle.brand,
  vehicle.trim,
  fuelLabels[vehicle.fuel],
  bodyLabels[vehicle.body],
  vehicle.keySpec,
  vehicle.tagline,
];

/** نمایش کلید میان‌بر درست برای مک و ویندوز؛ پیش از hydrate همان Ctrl */
function useShortcutLabel() {
  const [mac, setMac] = useState(false);
  useEffect(() => setMac(/Mac|iPhone|iPad/.test(navigator.platform)), []);
  return mac ? "⌘K" : "Ctrl K";
}

/**
 * دکمه‌ی آیکنی جستجو، هم‌شکل دکمه‌های کنارش؛ میان‌بر در راهنمای دکمه (title) است.
 * با نزدیک شدن نشانگر، فهرست خودروها از پیش گرفته می‌شود تا نتیجه‌ها بی‌درنگ بیایند.
 */
export function SiteSearch() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const shortcut = useShortcutLabel();
  const prefetch = () => void queryClient.prefetchQuery(vehiclesQuery);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
      // Ctrl+K همه‌جا؛ «/» فقط وقتی کاربر در کادری تایپ نمی‌کند. event.code تا با چیدمان فارسی هم کار کند
      if ((event.ctrlKey || event.metaKey) && event.code === "KeyK") {
        event.preventDefault();
        setOpen((value) => !value);
      } else if (event.key === "/" && !typing && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        onPointerEnter={prefetch}
        onFocus={prefetch}
        aria-label="جستجوی خودرو، برند یا خدمات"
        aria-keyshortcuts="Control+K /"
        aria-haspopup="dialog"
        title={`جستجو (${shortcut})`}
        data-slot="button"
        data-cta="outline"
        className={buttonVariants({ variant: "ghost", size: "icon-lg" })}
      >
        <HugeiconsIcon icon={Search01Icon} strokeWidth={1.8} className="size-5" />
      </button>
      <SearchDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function SearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        lang="fa"
        dir="rtl"
        showCloseButton={false}
        className="top-[max(1rem,12dvh)] max-h-[min(38rem,calc(100dvh-2rem))] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-xl"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>جستجو در تابان خودرو</DialogTitle>
          <DialogDescription>نام خودرو، برند، نوع سوخت یا خدمتی را بنویسید.</DialogDescription>
        </DialogHeader>
        {open ? <SearchBody onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function SearchBody({ onDone }: { onDone: () => void }) {
  const [term, setTerm] = useState("");
  const navigate = useNavigate();
  const vehicles = useQuery(vehiclesQuery);
  const destinations = useDestinations();
  const typed = term.trim() !== "";

  const all = vehicles.data?.vehicles ?? [];
  // «تست درایو» با فاصله باید «تست‌درایو» (نیم‌فاصله) را هم پیدا کند؛ یکسان‌سازی مشترک نیم‌فاصله را
  // حذف می‌کند ولی فاصله را نگه می‌دارد، پس عبارت بی‌فاصله هم امتحان می‌شود
  const hits = (fields: readonly string[]) =>
    matchesSearch(term, fields) || matchesSearch(term.replace(/\s+/g, ""), fields);
  // بی‌عبارت: چند مدل اول کاتالوگ (ترتیب مدیر محتوا) به‌عنوان پیشنهاد
  // نام یا برند خودرو پیش از مطابقت در توضیح و شعار می‌آید («نوا» اول نوا را بیاورد، نه خودرویی که
  // شعارش واژه‌ی نوا دارد)؛ sort پایدار است و ترتیب مدیر محتوا میان هم‌رده‌ها می‌ماند
  const nameFirst = (vehicle: SiteVehicle) =>
    hits([vehicle.name, vehicle.nameLatin, vehicle.brand]) ? 0 : 1;
  const foundVehicles = typed
    ? all
        .filter((vehicle) => hits(vehicleFields(vehicle)))
        .sort((a, b) => nameFirst(a) - nameFirst(b))
        .slice(0, 6)
    : all.slice(0, 4);
  const foundShortcuts = typed
    ? shortcuts.filter((item) => hits([item.label, item.hint]))
    : shortcuts.slice(0, fuelKeys.length);
  const foundPages = typed
    ? destinations.filter((item) => hits([item.label, item.hint, ...(item.keywords ?? [])]))
    : destinations.slice(0, 4);
  const nothing =
    typed &&
    !vehicles.isPending &&
    foundVehicles.length + foundShortcuts.length + foundPages.length === 0;

  const go = (item: Destination) => {
    onDone();
    if (item.external) window.open(item.href, "_blank", "noopener,noreferrer");
    else void navigate({ href: item.href });
  };
  const consult = () => {
    onDone();
    openConsult();
  };

  return (
    <Command shouldFilter={false} loop className="rounded-none! bg-transparent p-0">
      <div className="flex items-center gap-3 border-b px-4">
        <HugeiconsIcon
          icon={Search01Icon}
          strokeWidth={1.8}
          className="size-5 shrink-0 text-muted-foreground"
        />
        <CommandBareInput
          autoFocus
          value={term}
          onValueChange={setTerm}
          placeholder="نام خودرو، برند، برقی، شاسی‌بلند، رزرو سرویس…"
          aria-label="عبارت جستجو"
          className="h-14 w-full min-w-0 bg-transparent text-base outline-none placeholder:text-muted-foreground"
        />
        <Kbd className="hidden sm:inline-flex">Esc</Kbd>
        {/* روی گوشی Esc و «/» نیست؛ بستن جستجو باید دکمه‌ی دیدنی داشته باشد */}
        <button
          type="button"
          onClick={onDone}
          aria-label="بستن جستجو"
          className="grid size-11 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground sm:hidden"
        >
          <HugeiconsIcon icon={Cancel01Icon} strokeWidth={1.8} className="size-5" />
        </button>
      </div>

      <CommandList className="max-h-[min(30rem,calc(100dvh-8rem))] overscroll-contain p-2">
        {nothing ? (
          <CommandEmpty className="grid justify-items-center gap-3 px-4 py-8">
            <p className="text-sm text-muted-foreground">برای «{term.trim()}» نتیجه‌ای پیدا نشد.</p>
          </CommandEmpty>
        ) : null}

        {vehicles.isPending ? (
          <div className="grid gap-2 p-2" aria-hidden="true">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : foundVehicles.length > 0 ? (
          <CommandGroup heading={typed ? "خودروها" : "پیشنهاد برای شروع"}>
            {foundVehicles.map((vehicle) => (
              <CommandItem
                key={vehicle.id}
                value={`vehicle-${vehicle.id}`}
                onSelect={() =>
                  go({ label: vehicle.name, hint: "", href: `/vehicles/${vehicle.id}` })
                }
                className="gap-3 rounded-lg! py-2"
              >
                <span className="relative h-11 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
                  <Image src={vehicle.image} alt="" fill sizes="64px" className="object-cover" />
                </span>
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="truncate font-bold">
                    {vehicle.name}
                    <span className="font-normal text-muted-foreground">
                      {SEP}
                      {vehicle.trim}
                    </span>
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {/* تا جستجوی «camry» پیدا شود چرا این خودرو آمده */}
                    {vehicle.nameLatin ? `${vehicle.nameLatin}${SEP}` : null}
                    {fuelLabels[vehicle.fuel]}
                    {SEP}
                    {bodyLabels[vehicle.body]}
                    {vehicle.available ? null : `${SEP}فعلاً ناموجود`}
                  </span>
                </span>
                <span className="hidden shrink-0 text-sm font-bold sm:inline">
                  {platePriceWords(vehicle.price)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {foundShortcuts.length > 0 ? (
          <CommandGroup heading={typed ? "دسته‌ها" : "بر اساس نوع سوخت"}>
            {foundShortcuts.map((item) => (
              <DestinationItem key={item.href} item={item} onSelect={() => go(item)} />
            ))}
          </CommandGroup>
        ) : null}

        {foundPages.length > 0 ? (
          <CommandGroup heading="صفحه‌ها و خدمات">
            {foundPages.map((item) => (
              <DestinationItem key={item.href} item={item} onSelect={() => go(item)} />
            ))}
          </CommandGroup>
        ) : null}

        {/* همیشه راهی هست: کارشناس خودروی خاص یا سؤال بی‌جواب را پیدا می‌کند */}
        <CommandGroup heading={nothing ? "پیدا نکردید؟" : undefined}>
          <CommandItem value="consult" onSelect={consult} className="gap-3 rounded-lg! py-2.5">
            <span className="grid size-8 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
              <HugeiconsIcon icon={Message01Icon} strokeWidth={1.8} className="size-4" />
            </span>
            <span className="grid gap-0.5">
              <span className="font-bold">
                {typed ? `پرسیدن «${term.trim()}» از کارشناس فروش` : "مشاوره رایگان خرید"}
              </span>
              <span className="text-xs text-muted-foreground">
                فرم کوتاه همین‌جا باز می‌شود؛ کارشناس تماس می‌گیرد
              </span>
            </span>
          </CommandItem>
        </CommandGroup>
      </CommandList>

      <div className="hidden items-center gap-4 border-t px-4 py-2.5 text-xs text-muted-foreground sm:flex">
        <span className="inline-flex items-center gap-1.5">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          جابه‌جایی
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Kbd>Enter</Kbd>
          باز کردن
        </span>
      </div>
    </Command>
  );
}

function DestinationItem({ item, onSelect }: { item: Destination; onSelect: () => void }) {
  return (
    <CommandItem value={`page-${item.href}`} onSelect={onSelect} className="gap-3 rounded-lg! py-2">
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      <span className="shrink-0 text-xs text-muted-foreground">{item.hint}</span>
      <HugeiconsIcon
        icon={ArrowLeft02Icon}
        strokeWidth={1.8}
        className="size-4 shrink-0 text-muted-foreground"
      />
    </CommandItem>
  );
}
